"use strict";
/*
 * 8x8 Confidential & Proprietary || Copyright 2025 8x8, Inc. or its affiliates. All Rights Reserved.
 *  _____      _____
 * |  _  |    |  _  |
 *  \ V /__  __\ V /   ___ ___  _ __ ___
 *  / _ \\ \/ // _ \  / __/ _ \| '_ ` _ \
 * | |_| |>  <| |_| || (_| (_) | | | | | |
 * \_____/_/\_\_____(_)___\___/|_| |_| |_|
 *
 * @License https://www.8x8.com/terms-and-conditions/software-development-kit-license-agreement
 * @Author Comanici Paul
 * @Date 2025/08/28
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.cleanupFileSender = cleanupFileSender;
exports.createFileSendFunction = createFileSendFunction;
exports.getFileSender = getFileSender;
const tslib_1 = require("tslib");
const dependencies_1 = require("./dependencies");
const observability_codes_1 = require("./observability.codes");
const validator_1 = require("./validator");
const transformer_1 = require("./transformer");
const constants_1 = require("./constants");
class FileSender {
    constructor(instanceId, sendMessage) {
        this.activeTransfers = new Map();
        this.instanceId = instanceId;
        this.sendMessage = sendMessage;
    }
    sendFile(file, options = {}) {
        const fileId = options.fileId || this.generateFileId();
        // Check for duplicate fileId if custom fileId was provided
        if (options.fileId && this.activeTransfers.has(fileId)) {
            throw new Error(`${dependencies_1.ERROR_PREFIX} File transfer with ID '${fileId}' is already in progress`);
        }
        // Create the transfer promise
        const transferPromise = this.performFileTransfer(file, fileId, options);
        // Return control object immediately
        return {
            fileId,
            cancel: () => this.cancelTransfer(fileId),
            promise: transferPromise,
        };
    }
    performFileTransfer(file, fileId, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const startTime = Date.now();
            try {
                (0, dependencies_1.pushLog)(this.instanceId, [`Starting file send process for ${file.name}`], {
                    context: {
                        code: observability_codes_1.FILE_CODES.FILE_SEND_START,
                        fileId,
                        fileName: file.name,
                        fileSize: file.size,
                    },
                });
                // Validate file
                const validationResult = (0, validator_1.validateFile)(file, this.instanceId);
                if (!validationResult.isValid) {
                    const error = new Error(`${dependencies_1.ERROR_PREFIX} File validation failed: ${validationResult.errors.join(", ")}`);
                    this.handleError(fileId, file.name, error, observability_codes_1.FILE_CODES.FILE_VALIDATION_FAILED, options);
                    throw error;
                }
                // Log warnings if any
                if (validationResult.warnings.length > 0) {
                    (0, dependencies_1.pushLog)(this.instanceId, [`File validation warnings for ${file.name}: ${validationResult.warnings.join(", ")}`], {
                        context: { code: observability_codes_1.FILE_CODES.FILE_VALIDATION_WARNINGS, fileId, fileName: file.name },
                        level: dependencies_1.LOG_LEVEL_WARN,
                    });
                }
                // Transform file to chunks
                const { metadata, chunks } = yield (0, transformer_1.transformFileToChunks)(file, fileId, {}, this.instanceId);
                const chunkIntegrityValid = (0, transformer_1.validateChunkIntegrity)(chunks, file.size, this.instanceId);
                if (!chunkIntegrityValid) {
                    const error = new Error(`${dependencies_1.ERROR_PREFIX} Chunk integrity validation failed`);
                    this.handleError(fileId, file.name, error, observability_codes_1.FILE_CODES.CHUNK_INTEGRITY_FAILED, options);
                    throw error;
                }
                // Store active transfer
                this.activeTransfers.set(fileId, {
                    fileId,
                    file,
                    chunks,
                    metadata,
                    sentChunks: new Set(),
                    startTime,
                    options,
                });
                // Send initial file transfer request
                this.sendFileTransferRequest(fileId, metadata);
                // Start sending chunks
                const chunksSuccess = yield this.sendChunks(fileId);
                if (!chunksSuccess) {
                    const error = new Error(`${dependencies_1.ERROR_PREFIX} Chunk transfer failed`);
                    this.handleError(fileId, file.name, error, observability_codes_1.FILE_CODES.CHUNK_TRANSFER_FAILED, options);
                    throw error;
                }
            }
            catch (error) {
                this.handleError(fileId, file.name, error, observability_codes_1.FILE_CODES.FILE_SEND_FAILED, options);
                throw error;
            }
        });
    }
    cancelTransfer(fileId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const transfer = this.activeTransfers.get(fileId);
            if (!transfer) {
                return;
            }
            try {
                // Send cancel message
                this.sendMessage({
                    type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
                    message: {
                        action: constants_1.FILE_TRANSFER_ACTIONS.CANCEL,
                        value: {
                            fileId,
                            status: constants_1.FILE_TRANSFER_STATUS.FAILED,
                            error: "Transfer cancelled by user",
                        },
                    },
                });
                this.cleanup(fileId);
                (0, dependencies_1.pushLog)(this.instanceId, [`File transfer cancelled for ${transfer.file.name}`], {
                    context: { code: observability_codes_1.FILE_CODES.FILE_TRANSFER_CANCELLED, fileId, fileName: transfer.file.name },
                });
            }
            catch (error) {
                (0, dependencies_1.pushError)(this.instanceId, error, {
                    context: { code: observability_codes_1.FILE_CODES.FILE_CANCEL_ERROR, fileId },
                });
            }
        });
    }
    sendFileTransferRequest(fileId, metadata) {
        const request = {
            fileId,
            metadata,
        };
        this.sendMessage({
            type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
            message: {
                action: constants_1.FILE_TRANSFER_ACTIONS.SEND_FILE,
                value: request,
            },
        });
        (0, dependencies_1.track)(this.instanceId, dependencies_1.TRACK_MESSAGE, {
            type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
            eventName: constants_1.FILE_TRANSFER_ACTIONS.SEND_FILE,
            fileName: metadata.name,
            fileSize: metadata.size.toString(),
        });
    }
    sendChunks(fileId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const transfer = this.activeTransfers.get(fileId);
            if (!transfer) {
                (0, dependencies_1.pushError)(this.instanceId, new Error(`${dependencies_1.ERROR_PREFIX} Transfer not found for fileId: ${fileId}`), {
                    context: { code: observability_codes_1.FILE_CODES.TRANSFER_NOT_FOUND, fileId },
                });
                return false;
            }
            const { chunks } = transfer;
            for (let i = 0; i < chunks.length; i++) {
                const chunk = chunks[i];
                this.sendChunk(chunk);
                transfer.sentChunks.add(i);
                // Update progress
                this.updateProgress(fileId);
                // Add small delay between chunks to prevent overwhelming the channel
                if (i < chunks.length - 1) {
                    yield this.delay(10);
                }
            }
            // Send completion message
            this.sendCompletionMessage(fileId);
            return true;
        });
    }
    sendChunk(chunk) {
        const message = {
            type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
            message: {
                action: constants_1.FILE_TRANSFER_ACTIONS.CHUNK,
                value: chunk,
            },
        };
        // Extract transferable objects for zero-copy transfer
        const transferables = this.getTransferList(chunk);
        this.sendMessage(message, transferables);
    }
    sendCompletionMessage(fileId) {
        const transfer = this.activeTransfers.get(fileId);
        if (!transfer) {
            return;
        }
        const duration = Date.now() - transfer.startTime;
        this.sendMessage({
            type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
            message: {
                action: constants_1.FILE_TRANSFER_ACTIONS.COMPLETE,
                value: {
                    fileId,
                    status: constants_1.FILE_TRANSFER_STATUS.COMPLETED,
                    progress: 100,
                    transferredBytes: transfer.file.size,
                    totalBytes: transfer.file.size,
                },
            },
        });
        // Notify completion
        if (transfer.options.onComplete) {
            transfer.options.onComplete({
                fileId,
                fileName: transfer.file.name,
                success: true,
                transferredBytes: transfer.file.size,
                totalBytes: transfer.file.size,
                duration,
                hash: transfer.metadata.hash,
            });
        }
        (0, dependencies_1.track)(this.instanceId, dependencies_1.TRACK_MESSAGE, {
            type: dependencies_1.DATA_TYPE_FILE_TRANSFER,
            eventName: constants_1.FILE_TRANSFER_ACTIONS.COMPLETE,
            fileName: transfer.file.name,
            duration: duration.toString(),
        });
        (0, dependencies_1.pushLog)(this.instanceId, [`File transfer completed for ${transfer.file.name}`], {
            context: {
                code: observability_codes_1.FILE_CODES.FILE_TRANSFER_COMPLETED,
                fileId,
                fileName: transfer.file.name,
                duration,
                fileSize: transfer.file.size,
            },
        });
        this.cleanup(fileId);
    }
    updateProgress(fileId) {
        const transfer = this.activeTransfers.get(fileId);
        if (!transfer || !transfer.options.onProgress) {
            return;
        }
        const progress = (transfer.sentChunks.size / transfer.chunks.length) * 100;
        const transferredBytes = Array.from(transfer.sentChunks).reduce((sum, chunkIndex) => sum + transfer.chunks[chunkIndex].size, 0);
        transfer.options.onProgress({
            fileId,
            fileName: transfer.file.name,
            status: constants_1.FILE_TRANSFER_STATUS.UPLOADING,
            progress,
            transferredBytes,
            totalBytes: transfer.file.size,
            transferredChunks: transfer.sentChunks.size,
            totalChunks: transfer.chunks.length,
        });
    }
    handleError(fileId, fileName, error, code, options) {
        (0, dependencies_1.pushError)(this.instanceId, error, { context: { code, fileId, fileName } });
        if (options.onError) {
            options.onError({
                fileId,
                fileName,
                error: error.message,
                code,
            });
        }
        this.cleanup(fileId);
    }
    cleanup(fileId) {
        this.activeTransfers.delete(fileId);
    }
    generateFileId() {
        return `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    getTransferList(chunk) {
        const transferables = [];
        // Add chunk data if it's an ArrayBuffer
        if (chunk.data instanceof ArrayBuffer) {
            transferables.push(chunk.data);
        }
        // Add IV if it exists and is an ArrayBuffer
        if (chunk.iv instanceof ArrayBuffer) {
            transferables.push(chunk.iv);
        }
        return transferables;
    }
}
// Export singleton function to get FileSender instance for an instanceId
const fileSenders = new Map();
function getFileSender(instanceId) {
    if (!fileSenders.has(instanceId)) {
        const { sendMessage } = (0, dependencies_1.getInstance)(instanceId);
        if (!sendMessage) {
            (0, dependencies_1.pushError)(instanceId, new Error(`${dependencies_1.ERROR_PREFIX} SendMessage not available for instance ${instanceId}. Make sure init() was called first.`), {
                context: { code: observability_codes_1.FILE_CODES.FILESENDER_SENDMESSAGE_NOT_AVAILABLE },
            });
            return undefined;
        }
        fileSenders.set(instanceId, new FileSender(instanceId, sendMessage));
    }
    return fileSenders.get(instanceId);
}
function createFileSendFunction(instanceId) {
    return (file, options = {}) => {
        const fileSender = getFileSender(instanceId);
        if (!fileSender) {
            (0, dependencies_1.pushError)(instanceId, new Error(`${dependencies_1.ERROR_PREFIX} FileSender not available for instance ${instanceId}. Make sure init() was called first.`), {
                context: { code: observability_codes_1.FILE_CODES.FILESENDER_NOT_AVAILABLE },
            });
            return undefined;
        }
        return fileSender.sendFile(file, options);
    };
}
function cleanupFileSender(instanceId) {
    fileSenders.delete(instanceId);
}
//# sourceMappingURL=sender.js.map